/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useCallback, useEffect, useState } from "react";
import { View } from "react-native";
import { useLazyQuery } from "@apollo/client";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";

import { LoadingView, Text, styles } from "UpduoLibrary";
import CsvModal from "components/DashboardRoute/TableView/CsvModal";
import { skaleWebTextGray } from "config/UpduoColors";
import { useOnScrollToBottom } from "config/Utilities";
import {
  GET_STAFF_MEMBER_USER_LIST,
  GET_STAFF_USERPROXIES_LIST,
} from "graphql/internal/Queries";
import { RootState } from "store/types";
import { MembersTableTabType } from "./types";
import TableTabHeader from "./TableTabHeader";

import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function OnboardingRoute() {
  const dispatch = useDispatch();

  const authToken = useSelector((state: RootState) => state.authToken);
  const dayRangeStart = useSelector((state: RootState) => state.dayRangeStart);

  const [tabChoice, setTabChoice] = useState<MembersTableTabType>("members");
  const [searchString, setSearchString] = useState("");
  const [csvModalVisible, setCsvModalVisible] = useState(false);

  const [
    getStaffMemberUserList,
    {
      data: getStaffMemberUserListData,
      loading: getStaffMemberUserListLoading,
      error: getStaffMemberUserListError,
      fetchMore: fetchMoreMembers,
    },
  ] = useLazyQuery(GET_STAFF_MEMBER_USER_LIST, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [
    getStaffUserproxiesList,
    {
      data: getStaffUserproxiesListData,
      loading: getStaffUserproxiesListLoading,
      error: getStaffUserproxiesListError,
      fetchMore: fetchMoreUserproxies,
    },
  ] = useLazyQuery(GET_STAFF_USERPROXIES_LIST, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    _handleGetMemberUserList();
    _handleGetUnregisteredUserproxiesList();
  }, [searchString, authToken]);

  const _handleGetMemberUserList = useCallback(() => {
    UpduoAnalytics.logEvent("app-load-member-table");
    getStaffMemberUserList({
      variables: {
        cursor: null,
        getStaffMemberUserListRequest: { searchString },
      },
    });
  }, [searchString]);

  const _handleGetUnregisteredUserproxiesList = useCallback(() => {
    UpduoAnalytics.logEvent("app-load-userproxies-table");
    getStaffUserproxiesList({
      variables: {
        cursor: null,
        getStaffUserproxiesListRequest: { searchString },
      },
    });
  }, [searchString]);

  const _handleExportCsvButton = () => {
    setCsvModalVisible(true);
    UpduoAnalytics.logEvent("user-generate-csv");
  };

  useEffect(() => {
    if (getStaffMemberUserListData) {
      dispatch(
        ActionCreators.setMemberUserList(getStaffMemberUserListData.staffUsers)
      );
    }
  }, [getStaffMemberUserListData]);

  useEffect(() => {
    if (getStaffUserproxiesListData) {
      dispatch(
        ActionCreators.setUnregisteredUserproxiesList(
          getStaffUserproxiesListData.staffUserproxies
        )
      );
    }
  }, [getStaffUserproxiesListData]);

  useOnScrollToBottom(() => {
    if (
      tabChoice === "members" &&
      !getStaffMemberUserListLoading &&
      getStaffMemberUserListData?.staffUsers.pageInfo.hasNextPage
    ) {
      fetchMoreMembers({
        variables: {
          cursor: getStaffMemberUserListData.staffUsers.pageInfo.endCursor,
          getStaffMemberUserListRequest: { searchString },
        },
      });
    } else if (
      tabChoice === "unregisteredMembers" &&
      !getStaffUserproxiesListLoading &&
      getStaffUserproxiesListData?.staffUserproxies.pageInfo.hasNextPage
    ) {
      fetchMoreUserproxies({
        variables: {
          cursor:
            getStaffUserproxiesListData.staffUserproxies.pageInfo.endCursor,
          getStaffUserproxiesListRequest: { searchString },
        },
      });
    }
  });

  if (getStaffMemberUserListError) {
    const placeholderUsersList = {
      totalCount: 0,
      pageInfo: { hasNextPage: false },
      edges: [
        {
          node: {
            id: "",
            firstName: "",
            lastName: "",
            title: "",
            internalId: "",
            email: "",
            primaryEmail: "",
            hireDate: "",
            created: "",
            isCoach: false,
            isStaff: false,
            isTrainer: false,
            isAdmin: false,
            isManager: false,
            profileImgSmUrl: "",
            region: { id: "", name: "-" },
            district: { id: "", name: "-" },
            store: { id: "", name: "-" },
          },
        },
      ],
    };
    dispatch(ActionCreators.setMemberUserList(placeholderUsersList));
  }

  if (getStaffUserproxiesListError) {
    const placeholderUnregisteredUserproxiesList = {
      totalCount: 0,
      pageInfo: { hasNextPage: false },
      edges: [
        {
          node: {
            id: "",
            name: "Unassigned",
            hireDate: "",
            title: "",
            region: { id: "", name: "-" },
            district: { id: "", name: "-" },
            store: { id: "", name: "-" },
            email: "",
            internalId: "",
          },
        },
      ],
    };
    dispatch(
      ActionCreators.setUnregisteredUserproxiesList(
        placeholderUnregisteredUserproxiesList
      )
    );
  }

  const memberName = getStaffMemberUserListData
    ? `Members (${getStaffMemberUserListData?.staffUsers.totalCount})`
    : `Members`;

  return (
    <View style={{ flex: 1 }}>
      <View
        style={[styles.routeBodyContainer, { zIndex: 10, marginBottom: 60 }]}
      >
        <View
          style={{
            width: 200,
            paddingTop: 10,
            paddingBottom: 35,
            justifyContent: "center",
          }}
        >
          <Text
            style={{
              fontSize: 24,
              fontWeight: "bold",
              color: skaleWebTextGray,
            }}
          >
            {"Member List"}
          </Text>
        </View>
        <TableTabHeader
          memberName={memberName}
          tabChoice={tabChoice}
          onTabChoice={(tabChoice) => {
            setTabChoice(tabChoice);
          }}
          onChangeSearchText={(text) => {
            setSearchString(text);
          }}
          onPressExportCsvButton={_handleExportCsvButton}
        />
      </View>
      {getStaffMemberUserListLoading || getStaffUserproxiesListLoading ? (
        <LoadingView topSpacing={0} />
      ) : null}
      <CsvModal
        startDate={dayRangeStart || undefined}
        endDate={undefined}
        visible={csvModalVisible}
        onClose={() => setCsvModalVisible(false)}
        exportType={
          tabChoice === "members"
            ? "registered_members"
            : "unregistered_members"
        }
      />
    </View>
  );
}
